import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, Award, Target, Heart, Globe, CheckCircle, 
  Phone, MapPin, Clock, Star, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import AnimatedCounter from '@/components/common/AnimatedCounter';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

const values = [
  { icon: Heart, title: 'Client-Centric Approach', description: 'Your success is our priority. We provide personalized guidance tailored to your unique profile and goals.' },
  { icon: Target, title: 'Transparency', description: 'No hidden fees, no false promises. We maintain complete transparency throughout your recruitment journey.' },
  { icon: Award, title: 'Excellence', description: 'Our team of experts stays updated with the latest immigration policies to provide accurate guidance.' },
  { icon: Globe, title: 'Global Network', description: 'Extensive connections with employers and institutions across 50+ countries worldwide.' },
];

const timeline = [
  { year: '2010', title: 'Foundation', description: 'Zafco HR was established with a vision to connect Indian talent with global opportunities.' },
  { year: '2014', title: 'Expansion', description: 'Expanded services to cover 25+ countries including Canada, Australia, USA, and UK.' },
  { year: '2018', title: 'Recognition', description: 'Recognized as one of the top overseas recruitment consultancies in North India.' },
  { year: '2022', title: '10,000+ Placements', description: 'Achieved milestone of successfully placing over 10,000 professionals overseas.' },
  { year: '2026', title: 'Digital Transformation', description: 'Launched enhanced digital services for seamless client experience worldwide.' },
];

const AboutPage = () => {
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20need%20help%20with%20overseas%20recruitment', '_blank');
  };

  return (
    <div className="bg-slate-950" data-testid="about-page">
      <PageHeader 
        title="About Zafco Human Resource Management"
        subtitle="India's Trusted Partner for Overseas Manpower Recruitment Since 2010"
        breadcrumbs={[{ label: 'About Us' }]}
      />

      {/* Introduction Section */}
      <section className="py-20 bg-slate-950">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase">Who We Are</motion.span>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white font-syne mt-2 mb-6">
                Your Gateway to Global Career Success
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-6 leading-relaxed">
                Zafco Human Resource Management is a premier overseas manpower recruitment consultancy based in 
                New Delhi, India. Founded and led by <strong className="text-white">Mr. Zafar Abdul Wahhab</strong>, we have been helping 
                Indian professionals achieve their dreams of working abroad since 2010.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-6 leading-relaxed">
                With expertise in skilled migration, work visas, business immigration, and job placement services, 
                we have successfully assisted over 10,000 clients in securing employment opportunities across 
                Canada, Australia, USA, UK, Germany, UAE, and other top destination countries.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-8 leading-relaxed">
                Our commitment to ethical practices, transparent processes, and client success has made us one of 
                the most trusted names in the overseas recruitment industry in India. Explore our flagship{' '}
                <Link
                  to="/international-manpower-recruitment-europe-russia-israel-saudi-arabia"
                  className="text-[#C5A028] font-semibold underline decoration-2 underline-offset-2 hover:text-white transition-colors"
                  data-testid="about-link-to-intl-pillar"
                >
                  International Manpower Recruitment programme
                </Link>
                {' '}covering Saudi Arabia, Europe, Russia and Israel.
              </motion.p>

              <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-6">
                {[
                  { value: 30, label: 'Years Experience', suffix: '+' },
                  { value: 10000, label: 'Happy Clients', suffix: '+' },
                  { value: 50, label: 'Countries', suffix: '+' },
                  { value: 98, label: 'Success Rate', suffix: '%' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4 bg-slate-900/50 border border-white/5 rounded-xl">
                    <div className="text-3xl font-bold text-[#C5A028] font-syne">
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    </div>
                    <p className="text-slate-400 text-sm">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1565688527174-775059ac429c?w=800" 
                alt="ZAFCO immigration consultants in a strategy meeting at New Delhi office"
                className="rounded-2xl"
                width="800"
                height="600"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-slate-950/60 to-transparent" />
              <div className="absolute -bottom-8 -left-8 bg-slate-900 border border-white/10 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C5A028] to-[#EAB308] border-2 border-slate-900" />
                    ))}
                  </div>
                  <div>
                    <p className="font-bold text-white font-syne">Expert Team</p>
                    <p className="text-sm text-slate-400">50+ Immigration Specialists</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-900/30">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              className="bg-slate-900/50 border border-white/5 rounded-2xl p-8 hover:border-[#C5A028]/20 transition-colors"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 rounded-2xl bg-[#C5A028]/10 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#C5A028]" />
              </div>
              <h3 className="text-2xl font-bold text-white font-syne mb-4">Our Mission</h3>
              <p className="text-slate-400 leading-relaxed">
                To empower Indian professionals with opportunities to build successful international careers 
                through ethical, transparent, and expert recruitment services. We strive to be the bridge 
                connecting talent with global employers, making overseas employment accessible and hassle-free.
              </p>
            </motion.div>

            <motion.div
              className="bg-slate-900/50 border border-white/5 rounded-2xl p-8 hover:border-[#C5A028]/20 transition-colors"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-[#C5A028]/10 flex items-center justify-center mb-6">
                <Star className="w-8 h-8 text-[#C5A028]" />
              </div>
              <h3 className="text-2xl font-bold text-white font-syne mb-4">Our Vision</h3>
              <p className="text-slate-400 leading-relaxed">
                To become India's most trusted overseas recruitment consultancy, recognized globally for 
                our commitment to client success, ethical practices, and innovative solutions. We envision 
                a world where geographical boundaries don't limit career growth and opportunities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-slate-950">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-syne mt-2 mb-4">
              What Drives Us Forward
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="text-center p-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <motion.div 
                  className="w-20 h-20 rounded-full bg-[#C5A028]/10 flex items-center justify-center mx-auto mb-6"
                  whileHover={{ scale: 1.1, backgroundColor: "rgba(197, 160, 40, 0.2)" }}
                >
                  <value.icon className="w-10 h-10 text-[#C5A028]" />
                </motion.div>
                <h3 className="text-xl font-bold text-white font-syne mb-3">{value.title}</h3>
                <p className="text-slate-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-slate-900/30">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase">Our Journey</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-syne mt-2 mb-4">
              Milestones That Define Us
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                className="flex gap-8 mb-12 last:mb-0"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#C5A028] to-[#EAB308] flex items-center justify-center text-black font-bold font-syne">
                    {item.year}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gradient-to-b from-[#C5A028] to-[#C5A028]/20 mt-4" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <h3 className="text-xl font-bold text-white font-syne mb-2">{item.title}</h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Location */}
      <section className="py-20 bg-slate-950">
        <div className="container max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-[#C5A028] font-semibold text-sm tracking-wider uppercase">Visit Us</motion.span>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white font-syne mt-2 mb-6">
                Our New Delhi Office
              </motion.h2>
              
              <motion.div variants={fadeInUp} className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C5A028]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#C5A028]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Address</h4>
                    <p className="text-slate-400">
                      Ground Floor 5, Building No. 27B,<br />
                      Khizrabad, New Friends Colony,<br />
                      New Delhi - 110025
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C5A028]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#C5A028]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Phone</h4>
                    <a 
                      href="https://wa.me/919958848998"
                      className="text-slate-400 hover:text-[#C5A028] transition-colors"
                    >
                      +91-9958848998
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C5A028]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#C5A028]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">Working Hours</h4>
                    <p className="text-slate-400">
                      Monday - Saturday: 10:00 AM - 7:00 PM<br />
                      Sunday: By Appointment Only
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Button 
                  onClick={openWhatsApp}
                  className="bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-8 py-6 rounded-full"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Schedule a Visit
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-premium rounded-2xl p-8"
            >
              <h3 className="text-xl font-bold text-white font-syne mb-6">
                Book Your Free Consultation
              </h3>
              <ConsultationForm variant="dark" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
